import { useEffect, useMemo } from 'react';
import { useTemplateId, useTemplateVisibility } from './useTemplate';
import { useUser } from './useUser';
import { useGetTemplateAccessGroupsQuery } from 'rtk/query/apis/templates';
import { getMissingTemplateAccessGroups } from 'utils/templateAccessGroups';
import { isRecruiterUser } from 'types/user.types';

export type TMissingTemplateAccessGroups = {
  /** True when the recruiter can't create questions in any of the template's access groups. */
  isCreateBlocked: boolean;
  /**
   * True while the template's access groups are still in flight, i.e. we don't yet know whether the
   * action is allowed. Entry points should show a loader rather than let a click through on a
   * not-yet-known answer.
   */
  isResolvingAccessGroups: boolean;
  missingAccessGroupNames: string[];
  /** Ready-to-render list for the "contact your admin" message. */
  missingAccessGroupLabel: string;
};

/**
 * Whether the current user is blocked from creating a question in the template, and which groups to
 * name — see `getMissingTemplateAccessGroups`. Only meaningful inside the template builder;
 * outside it there is no template in the store and nothing is ever missing.
 */
export const useMissingTemplateAccessGroups = (): TMissingTemplateAccessGroups => {
  const user = useUser();
  const templateId = useTemplateId();
  const visibility = useTemplateVisibility();

  // Deliberately NOT the store's accessGroupIds: those are the raw access-group resource rows, which
  // outlive the group they point at, so a soft-deleted group would be named as one to get rights in.
  // This endpoint returns only groups that still exist — and it is also the only source of names for
  // groups the user isn't a member of (their own are on the user object).
  const shouldResolve = !!templateId && isRecruiterUser(user) && visibility === 'local';
  const { data, error } = useGetTemplateAccessGroupsQuery({ templateId }, { skip: !shouldResolve });

  // Fail open on a lookup error rather than block on unknown data — the backend enforces this
  // regardless. Logged because a silently absent gate is otherwise indistinguishable from a
  // recruiter who genuinely can create in one of the groups.
  useEffect(() => {
    if (error) {
      console.error('Failed to load template access groups', error);
    }
  }, [error]);

  const missing = useMemo(
    () => getMissingTemplateAccessGroups(user, visibility, data?.accessGroups),
    [user, visibility, data?.accessGroups],
  );

  return {
    isCreateBlocked: missing.length > 0,
    // Derived from the absence of a result rather than RTK's isLoading so the very first render —
    // before the query is even kicked off — also counts as unresolved. An error resolves it (fail
    // open), matching the missing-groups calculation above.
    isResolvingAccessGroups: shouldResolve && !data && !error,
    missingAccessGroupNames: missing.map((ag) => ag.name),
    missingAccessGroupLabel: missing.map((ag) => ag.name).join(', '),
  };
};
